import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useSession } from "../../session/SessionContext";
import { AdminGate } from "../../components/admin/AdminGate";
import { useToast } from "../../components/toast/toastContext";
import { Tabs, type TabDefinition } from "../../components/admin/Tabs";
import { MemberAddPanel, MemberTable } from "../../components/admin/MemberCollectionEditor";
import { EmptyState, ErrorState, LoadingState } from "../../components/states";
import { formatPersonName } from "../../components/shared/personDisplay";
import styles from "../../components/admin/Admin.module.css";
import roleStyles from "./RolesPage.module.css";
import type { PersonSummary as AdminPersonSummary } from "../../api/entitlementAdmin";
import { fetchPeople } from "../../api/entitlementAdmin";
import { searchImpersonationTargets, type PersonSummary as SearchPersonSummary } from "../../api/session";
import { assignRole, createRole, deleteRole, fetchPermissions, fetchRoles, renameRole, revokeRole, setRolePermissions, type PermissionDefinition, type RoleBundle } from "../../api/roles";

const BundleAdministerPermission = "roles.administer";

/** The tab that creates a role. Its address is `/admin/roles/add-role`. */
const AddRoleTabId = "add-role";
const AssignPermission = "entitlement.administer";

interface PermissionMetadata {
  nameKey: string;
  descriptionKey: string;
}

const PermissionCatalog: Record<string, PermissionMetadata> = {
  "consumption.read.others": {
    nameKey: "admin.roles.permissionCatalog.consumptionReadOthers.name",
    descriptionKey: "admin.roles.permissionCatalog.consumptionReadOthers.description",
  },
  "consumption.read.org": {
    nameKey: "admin.roles.permissionCatalog.consumptionReadOrg.name",
    descriptionKey: "admin.roles.permissionCatalog.consumptionReadOrg.description",
  },
  "entitlement.administer": {
    nameKey: "admin.roles.permissionCatalog.entitlementAdminister.name",
    descriptionKey: "admin.roles.permissionCatalog.entitlementAdminister.description",
  },
  "scope.global": {
    nameKey: "admin.roles.permissionCatalog.allowGlobalScope.name",
    descriptionKey: "admin.roles.permissionCatalog.allowGlobalScope.description",
  },
  "groups.administer": {
    nameKey: "admin.roles.permissionCatalog.groupsAdminister.name",
    descriptionKey: "admin.roles.permissionCatalog.groupsAdminister.description",
  },
  "request.approve": {
    nameKey: "admin.roles.permissionCatalog.requestApprove.name",
    descriptionKey: "admin.roles.permissionCatalog.requestApprove.description",
  },
  "request.approve.any": {
    nameKey: "admin.roles.permissionCatalog.requestApproveAny.name",
    descriptionKey: "admin.roles.permissionCatalog.requestApproveAny.description",
  },
  "settings.write": {
    nameKey: "admin.roles.permissionCatalog.settingsWrite.name",
    descriptionKey: "admin.roles.permissionCatalog.settingsWrite.description",
  },
  "audit.read": {
    nameKey: "admin.roles.permissionCatalog.auditRead.name",
    descriptionKey: "admin.roles.permissionCatalog.auditRead.description",
  },
  "identity.impersonate": {
    nameKey: "admin.roles.permissionCatalog.identityImpersonate.name",
    descriptionKey: "admin.roles.permissionCatalog.identityImpersonate.description",
  },
  "accounts.administer": {
    nameKey: "admin.roles.permissionCatalog.accountsAdminister.name",
    descriptionKey: "admin.roles.permissionCatalog.accountsAdminister.description",
  },
  "roles.administer": {
    nameKey: "admin.roles.permissionCatalog.rolesAdminister.name",
    descriptionKey: "admin.roles.permissionCatalog.rolesAdminister.description",
  },
  "organisation.import": {
    nameKey: "admin.roles.permissionCatalog.organisationImport.name",
    descriptionKey: "admin.roles.permissionCatalog.organisationImport.description",
  },
  "request.join-team": {
    nameKey: "admin.roles.permissionCatalog.requestJoinTeam.name",
    descriptionKey: "admin.roles.permissionCatalog.requestJoinTeam.description",
  },
  "budgets.backdate": {
    nameKey: "admin.roles.permissionCatalog.budgetsBackdate.name",
    descriptionKey: "admin.roles.permissionCatalog.budgetsBackdate.description",
  },
  "providers.administer": {
    nameKey: "admin.roles.permissionCatalog.providersAdminister.name",
    descriptionKey: "admin.roles.permissionCatalog.providersAdminister.description",
  },
  "bedrock.models.manage": {
    nameKey: "admin.roles.permissionCatalog.bedrockModelsManage.name",
    descriptionKey: "admin.roles.permissionCatalog.bedrockModelsManage.description",
  },
  "configuration.import-export": {
    nameKey: "admin.roles.permissionCatalog.configurationImportExport.name",
    descriptionKey: "admin.roles.permissionCatalog.configurationImportExport.description",
  },
};

/**
 * The sections the permissions are shown in, each the permissions that belong together, in the order a person would look for them.
 * A permission the server defines and no section names is shown under "Other", so a new one is never hidden.
 */
const PermissionGroups: readonly { id: string; keys: readonly string[] }[] = [
  { id: "usage", keys: ["consumption.read.org", "consumption.read.others"] },
  { id: "people", keys: ["entitlement.administer", "scope.global", "identity.impersonate"] },
  { id: "requests", keys: ["request.join-team", "request.approve", "request.approve.any"] },
  { id: "organisation", keys: ["groups.administer", "organisation.import", "budgets.backdate"] },
  { id: "providers", keys: ["providers.administer", "bedrock.models.manage"] },
  { id: "system", keys: ["roles.administer", "accounts.administer", "settings.write", "audit.read", "configuration.import-export"] },
];

/**
 * The permissions the server defines decide what can be granted. PermissionCatalog only supplies translated names and
 * descriptions for the keys it knows; any other key is shown by its key and the server's description.
 */
type PageState =
  | { kind: "loading" }
  | { kind: "loaded"; roles: RoleBundle[]; permissions: PermissionDefinition[] }
  | { kind: "error"; message: string };

export function RolesPage(): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div>
      <AdminGate permissions={[BundleAdministerPermission]} mode="any" title={t("admin.roles.denied.title")} description={t("admin.roles.denied.description")}>
        <RolesContent />
      </AdminGate>
    </div>
  );
}

function RolesContent(): React.JSX.Element {
  const { t } = useTranslation();
  const { snapshot } = useSession();
  const navigate = useNavigate();
  const { tab } = useParams<{ tab: string }>();
  const [state, setState] = useState<PageState>({ kind: "loading" });

  const canAssign = snapshot.status === "signed-in" && snapshot.permissions.includes(AssignPermission);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const [rolesResult, permissionsResult] = await Promise.all([fetchRoles(), fetchPermissions()]);
    if (rolesResult.kind === "ok" && permissionsResult.kind === "ok") {
      setState({ kind: "loaded", roles: rolesResult.response, permissions: permissionsResult.response });
    } else if (rolesResult.kind === "denied" || permissionsResult.kind === "denied") {
      setState({ kind: "error", message: t("admin.roles.denied.description") });
    } else {
      const failure = rolesResult.kind === "ok" ? permissionsResult : rolesResult;
      setState({ kind: "error", message: "message" in failure ? failure.message : t("admin.roles.denied.description") });
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.kind === "loading") {
    return <LoadingState label={t("admin.roles.loading")} />;
  }

  if (state.kind === "error") {
    return <ErrorState title={t("admin.roles.errorTitle")} description={state.message} onRetry={() => void load()} />;
  }

  // A role's tab is addressed by the role's id, so `/admin/roles/<role id>` opens that role directly.
  const tabs: TabDefinition[] = [
    ...state.roles.map((role) => ({
      id: role.id,
      label: role.name,
      content: <RoleCard role={role} permissions={state.permissions} canAssign={canAssign} onChanged={() => void load()} />,
    })),
    {
      id: AddRoleTabId,
      label: t("admin.roles.addRole"),
      content: <CreateRoleTab showEmptyState={state.roles.length === 0} onCreated={() => void load()} />,
    },
  ];

  if (tab === undefined || !tabs.some((candidate) => candidate.id === tab)) {
    return <Navigate to={`/admin/roles/${tabs[0].id}`} replace />;
  }

  return <Tabs tabs={tabs} activeId={tab} onSelect={(id) => void navigate(`/admin/roles/${id}`)} />;
}

function CreateRoleTab({ showEmptyState, onCreated }: { showEmptyState: boolean; onCreated: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleDescription, setNewRoleDescription] = useState("");

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    const result = await createRole(newRoleName, newRoleDescription);
    if (result.kind === "ok") {
      setNewRoleName("");
      setNewRoleDescription("");
      onCreated();
    } else if (result.kind === "denied") {
      toast.show(t("admin.denied.description", { permissions: BundleAdministerPermission }), "error");
    } else {
      toast.show(result.message, "error");
    }
  };

  return (
    <section className={styles.section} aria-labelledby="add-role-heading">
      <h2 id="add-role-heading">{t("admin.roles.addRole")}</h2>
      <form className={styles.form} onSubmit={(event) => void handleCreate(event)}>
        <div className={styles.field}>
          <label htmlFor="new-role-name">{t("admin.roles.form.name")}</label>
          <input id="new-role-name" value={newRoleName} onChange={(event) => setNewRoleName(event.target.value)} required />
        </div>
        <div className={`${styles.field} ${styles.descriptionField}`}>
          <label htmlFor="new-role-description">{t("admin.roles.form.description")}</label>
          <textarea id="new-role-description" rows={4} value={newRoleDescription} onChange={(event) => setNewRoleDescription(event.target.value)} required />
        </div>
        <button type="submit" className="button button-primary">
          {t("admin.roles.form.create")}
        </button>
      </form>
      {showEmptyState && <EmptyState title={t("admin.roles.emptyTitle")} description={t("admin.roles.emptyDescription")} />}
    </section>
  );
}

function RoleCard({ role, permissions, canAssign, onChanged }: { role: RoleBundle; permissions: PermissionDefinition[]; canAssign: boolean; onChanged: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description);
  const [selectedPermissions, setSelectedPermissions] = useState(role.permissionKeys);
  const [roleMembers, setRoleMembers] = useState<AdminPersonSummary[]>([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [memberSearchResults, setMemberSearchResults] = useState<SearchPersonSummary[]>([]);
  const [memberSearchError, setMemberSearchError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const searchDebounceRef = useRef<number | null>(null);
  const permissionKeys = [...new Set([...permissions.map((permission) => permission.key), ...role.permissionKeys])];
  const serverDescriptions = new Map(permissions.map((permission) => [permission.key, permission.description]));

  const getPermissionDisplay = (key: string) => {
    const metadata = PermissionCatalog[key];
    if (!metadata) {
      return { name: key, description: serverDescriptions.get(key) ?? key };
    }

    return {
      name: t(metadata.nameKey),
      description: t(metadata.descriptionKey),
    };
  };

  const loadRoleMembers = useCallback(async () => {
    const result = await fetchPeople(undefined, undefined, 0, 100, role.id);

    if (result.kind === "ok") {
      setRoleMembers(Array.isArray(result.response.items) ? result.response.items : []);
      return;
    }

    if (result.kind === "denied") {
      setRoleMembers([]);
      toast.show(t("states.permissionDenied.defaultDescription"), "error");
      return;
    }

    setRoleMembers([]);
    toast.show(result.message, "error");
  }, [role.id, t, toast]);

  useEffect(() => {
    void loadRoleMembers();
  }, [loadRoleMembers]);

  const memberSearchTerm = memberSearch.trim();

  useEffect(() => {
    if (memberSearchTerm.length === 0) {
      setMemberSearchResults([]);
      setMemberSearchError(null);
      return;
    }

    if (searchDebounceRef.current !== null) {
      window.clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = window.setTimeout(() => {
      void (async () => {
        const result = await searchImpersonationTargets(memberSearchTerm);
        if (result.kind === "ok") {
          setMemberSearchResults(result.response.items.filter((person) => !roleMembers.some((member) => member.id === person.id)));
          setMemberSearchError(null);
          return;
        }

        if (result.kind === "denied") {
          setMemberSearchError(t("states.permissionDenied.defaultDescription"));
          return;
        }

        setMemberSearchError(result.message);
      })();
    }, 250);

    return () => {
      if (searchDebounceRef.current !== null) {
        window.clearTimeout(searchDebounceRef.current);
      }
    };
  }, [memberSearchTerm, roleMembers, t]);

  const handleMemberAdd = async (person: SearchPersonSummary) => {
    const result = await assignRole(person.id, role.id);
    if (result.kind === "ok") {
      toast.show(t("admin.roles.assignSuccess"), "success");
      setMemberSearch("");
      setMemberSearchResults([]);
      await loadRoleMembers();
      return;
    }

    if (result.kind === "denied") {
      toast.show(t("admin.denied.description", { permissions: AssignPermission }), "error");
      return;
    }

    toast.show(result.message, "error");
  };

  const handleMemberRemove = async (person: AdminPersonSummary) => {
    const result = await revokeRole(person.id, role.id);
    if (result.kind === "ok") {
      toast.show(t("admin.roles.revokeSuccess"), "success");
      setMemberSearch("");
      setMemberSearchResults([]);
      await loadRoleMembers();
      return;
    }

    if (result.kind === "denied") {
      toast.show(t("admin.denied.description", { permissions: AssignPermission }), "error");
      return;
    }

    if (result.kind === "conflict") {
      toast.show(t("admin.roles.lastAdministrator", { detail: result.message }), "error");
      return;
    }

    toast.show(result.message, "error");
  };

  const handleRename = async (event: React.FormEvent) => {
    event.preventDefault();
    const result = await renameRole(role.id, name, description);
    if (result.kind === "ok") {
      toast.show(t("admin.roles.saved"), "success");
      onChanged();
    } else if (result.kind === "denied") {
      toast.show(t("admin.denied.description", { permissions: BundleAdministerPermission }), "error");
    } else {
      toast.show(result.message, "error");
    }
  };

  const handleSetPermissions = async (event: React.FormEvent) => {
    event.preventDefault();
    const desired = selectedPermissions;
    const add = desired.filter((key) => !role.permissionKeys.includes(key));
    const remove = role.permissionKeys.filter((key) => !desired.includes(key));
    const result = await setRolePermissions(role.id, add, remove);
    if (result.kind === "ok") {
      toast.show(t("admin.roles.saved"), "success");
      onChanged();
    } else if (result.kind === "denied") {
      toast.show(t("admin.denied.description", { permissions: BundleAdministerPermission }), "error");
    } else {
      toast.show(result.message, "error");
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    const result = await deleteRole(role.id);
    setIsDeleting(false);
    if (result.kind === "ok") {
      setShowDeleteConfirm(false);
      setDeleteConfirmInput("");
      onChanged();
    } else if (result.kind === "denied") {
      toast.show(t("admin.denied.description", { permissions: BundleAdministerPermission }), "error");
    } else {
      toast.show(result.message, "error");
    }
  };

  const formatMemberLabel = (person: AdminPersonSummary | SearchPersonSummary) => {
    const display = formatPersonName(person);
    return display === person.email ? person.email : `${display} (${person.email})`;
  };

  const permissionGroups = [
    ...PermissionGroups.map((group) => ({ id: group.id, keys: group.keys.filter((key) => permissionKeys.includes(key)) })),
    { id: "other", keys: permissionKeys.filter((key) => !PermissionGroups.some((group) => group.keys.includes(key))) },
  ].filter((group) => group.keys.length > 0);
  const grantedCount = permissionKeys.filter((key) => selectedPermissions.includes(key)).length;
  const permissionsChanged =
    selectedPermissions.length !== role.permissionKeys.length || selectedPermissions.some((key) => !role.permissionKeys.includes(key));
  const togglePermission = (key: string, granted: boolean) =>
    setSelectedPermissions((current) => (granted ? [...current, key] : current.filter((permission) => permission !== key)));

  return (
    <div className={roleStyles.layout}>
      <div className={roleStyles.top}>
        <section className={roleStyles.card} aria-labelledby={`role-${role.id}-details-heading`}>
          <h3 id={`role-${role.id}-details-heading`} className={`panel-title ${roleStyles.cardTitle}`}>
            {t("admin.roles.details")}
          </h3>
          <form className={roleStyles.detailsForm} onSubmit={(event) => void handleRename(event)}>
            <div className={styles.field}>
              <label htmlFor={`role-${role.id}-name`}>{t("admin.roles.form.name")}</label>
              <input id={`role-${role.id}-name`} value={name} onChange={(event) => setName(event.target.value)} required readOnly={role.isAutomatic} />
            </div>
            <div className={`${styles.field} ${styles.descriptionField}`}>
              <label htmlFor={`role-${role.id}-description`}>{t("admin.roles.form.description")}</label>
              <textarea id={`role-${role.id}-description`} rows={3} value={description} onChange={(event) => setDescription(event.target.value)} required readOnly={role.isAutomatic} />
            </div>
            {!role.isAutomatic && (
              <div>
                <button type="submit" className="button button-secondary" disabled={name === role.name && description === role.description}>
                  {t("admin.roles.form.save")}
                </button>
              </div>
            )}
          </form>
        </section>

        <section className={roleStyles.card} aria-labelledby={`role-${role.id}-people-heading`}>
          <h3 id={`role-${role.id}-people-heading`} className={`panel-title ${roleStyles.cardTitle}`}>
            {t("admin.roles.people")}
            <span className={roleStyles.countChip}>{roleMembers.length}</span>
          </h3>
          {role.isAutomatic ? (
            <p className={styles.note}>{t("admin.roles.automaticMembers")}</p>
          ) : canAssign ? (
            <MemberAddPanel
              noResultsMessage={t("admin.roles.noMatchingUsers")}
              searchLabel={t("admin.roles.addUser")}
              searchPlaceholder={t("admin.roles.addUserPlaceholder")}
              addButtonLabel={t("admin.roles.addUser")}
              searchId={`role-${role.id}-member-search`}
              searchValue={memberSearch}
              searchError={memberSearchError}
              searchResults={memberSearchResults}
              onSearchChange={setMemberSearch}
              onAdd={handleMemberAdd}
              formatLabel={formatMemberLabel}
            />
          ) : (
            <p className={styles.note}>{t("admin.roles.assignNeeded")}</p>
          )}
          {(canAssign || role.isAutomatic) && (
            <MemberTable
              title={t("admin.roles.assignedUsersTitle")}
              emptyMessage={t("admin.roles.assignedUsersEmpty")}
              removeButtonLabel={t("admin.roles.removeUser")}
              members={roleMembers}
              formatLabel={formatMemberLabel}
              onRemove={handleMemberRemove}
              readOnly={role.isAutomatic}
            />
          )}
        </section>
      </div>

      <form className={roleStyles.card} aria-labelledby={`role-${role.id}-permissions-heading`} onSubmit={(event) => void handleSetPermissions(event)}>
        <div className={roleStyles.permissionsHeader}>
          <h3 id={`role-${role.id}-permissions-heading`} className={`panel-title ${roleStyles.cardTitle}`}>
            {t("admin.roles.form.permissions")}
          </h3>
          <span className={roleStyles.summary}>{t("admin.roles.permissionsSummary", { granted: grantedCount, total: permissionKeys.length })}</span>
        </div>
        <div className={roleStyles.groups}>
          {permissionGroups.map((group) => {
            const grantedInGroup = group.keys.filter((key) => selectedPermissions.includes(key)).length;
            return (
              <fieldset key={group.id} className={roleStyles.group}>
                <legend className={roleStyles.groupTitle}>
                  <span>{t(`admin.roles.groups.${group.id}`)}</span>
                  <span className={roleStyles.groupCount}>{t("admin.roles.groupCount", { granted: grantedInGroup, total: group.keys.length })}</span>
                </legend>
                <ul className={roleStyles.permissionList}>
                  {group.keys.map((key) => {
                    const display = getPermissionDisplay(key);
                    const descriptionId = `role-${role.id}-permission-${key}-description`;
                    const granted = selectedPermissions.includes(key);
                    return (
                      <li key={key} className={roleStyles.permission} data-granted={granted || undefined}>
                        <label className={roleStyles.permissionLabel}>
                          <input
                            type="checkbox"
                            className={roleStyles.switch}
                            checked={granted}
                            aria-describedby={descriptionId}
                            onChange={(event) => togglePermission(key, event.target.checked)}
                          />
                          <span className={roleStyles.permissionName}>{display.name}</span>
                        </label>
                        <p id={descriptionId} className={roleStyles.permissionDescription}>
                          {display.description}
                        </p>
                        <code className={roleStyles.permissionKey}>{key}</code>
                      </li>
                    );
                  })}
                </ul>
              </fieldset>
            );
          })}
        </div>
        {/* Kept in view at the foot of the page while there is something to save, so a long list never hides the button. */}
        <div className={roleStyles.saveBar} data-changed={permissionsChanged || undefined}>
          <span className={roleStyles.saveNote}>{permissionsChanged ? t("admin.roles.unsavedChanges") : t("admin.roles.noChanges")}</span>
          {permissionsChanged && (
            <button type="button" className="button button-secondary" onClick={() => setSelectedPermissions(role.permissionKeys)}>
              {t("admin.roles.discardChanges")}
            </button>
          )}
          <button type="submit" className="button button-primary" disabled={!permissionsChanged}>
            {t("admin.roles.form.savePermissions")}
          </button>
        </div>
      </form>

      {roleMembers.length === 0 && !role.isAutomatic && (
        <section className={`${roleStyles.card} ${roleStyles.danger}`} aria-labelledby={`role-${role.id}-danger-heading`}>
          <h3 id={`role-${role.id}-danger-heading`} className={`panel-title ${roleStyles.cardTitle}`}>
            {t("admin.roles.dangerZone")}
          </h3>
          <p className={roleStyles.dangerText}>{t("admin.roles.form.deleteWarning")}</p>
          {!showDeleteConfirm ? (
            <div>
              <button type="button" className="button button-danger" onClick={() => setShowDeleteConfirm(true)}>
                {t("admin.roles.form.delete")}
              </button>
            </div>
          ) : (
            <form
              className={roleStyles.deleteForm}
              onSubmit={(event) => {
                event.preventDefault();
                if (deleteConfirmInput === role.name) {
                  void handleDelete();
                } else {
                  toast.show(t("admin.roles.form.deleteMismatch"), "error");
                }
              }}
            >
              <div className={styles.field}>
                <label htmlFor={`role-${role.id}-delete-confirm`}>{t("admin.roles.form.deleteInput")}</label>
                <input
                  id={`role-${role.id}-delete-confirm`}
                  type="text"
                  value={deleteConfirmInput}
                  onChange={(event) => setDeleteConfirmInput(event.target.value)}
                  placeholder={role.name}
                  aria-describedby={`role-${role.id}-delete-hint`}
                  autoFocus
                />
                <small id={`role-${role.id}-delete-hint`} className={roleStyles.hint}>
                  {t("admin.roles.form.deleteTypeToConfirm", { roleName: role.name })}
                </small>
              </div>
              <div className={roleStyles.buttons}>
                <button type="submit" className="button button-danger" disabled={deleteConfirmInput !== role.name || isDeleting}>
                  {isDeleting ? t("admin.roles.form.deleting") : t("admin.roles.form.deleteConfirm")}
                </button>
                <button
                  type="button"
                  className="button button-secondary"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmInput("");
                  }}
                  disabled={isDeleting}
                >
                  {t("admin.roles.form.cancel")}
                </button>
              </div>
            </form>
          )}
        </section>
      )}
    </div>
  );
}
